import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { theme } from "../../../../theme";
import { type ShiftTask, ICreateTask, IUpdateTask, TaskCategory } from "@/logic/api";

interface AddEditTaskModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (task: ICreateTask | IUpdateTask) => Promise<void>;
    task?: ShiftTask;
    isLoading?: boolean;
    category?: TaskCategory;
}

export const AddEditTaskModal: React.FC<AddEditTaskModalProps> = ({
    open,
    onClose,
    onSave,
    task,
    isLoading = false,
    category = TaskCategory.NonDriving
}) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        title: "",
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [saveError, setSaveError] = useState<string>("");
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (open) {
            setIsEditMode(Boolean(task?.key));
            if (task) {
                setFormData({
                    title: task.title || "",
                });
            } else {
                setFormData({
                    title: "",
                });
            }
            setErrors({});
            setSaveError("");
        }
    }, [open, task]);

    const hasChanges = open && (
        !isEditMode ||
        formData.title !== (task?.title || "")
    );

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.title.trim()) {
            newErrors.title = t("task.validations.taskTitleRequired");
        } else if (formData.title.length > 255) {
            newErrors.title = t("task.validations.taskTitleLength");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaveError("");
            const taskData = isEditMode && task?.key
                ? { key: task.key, title: formData.title.trim() }
                : { title: formData.title.trim(), category };

            await onSave(taskData);
            onClose();
        } catch (error) {
            console.error("Error saving task:", error);
            setSaveError(error instanceof Error ? error.message : "Failed to save task");
        }
    };

    const handleFieldChange = (field: keyof typeof formData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            data-testid="add-edit-task-modal"
        >
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
                        {isEditMode ? t("task.management.editTask") : t("task.management.addNewTask")}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {saveError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {saveError}
                    </Alert>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                            {t("task.fields.taskTitle")} <span style={{ color: theme.palette.error.main }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder={t("task.fields.taskTitlePlaceholder")}
                            value={formData.title}
                            onChange={(e) => handleFieldChange("title", e.target.value)}
                            error={Boolean(errors.title)}
                            helperText={errors.title || ""}
                            disabled={isLoading}
                            data-testid="task-title-input"
                            size="small"
                            variant="outlined"
                            autoComplete="off"
                            slotProps={{
                                htmlInput: {
                                    sx: {
                                        maxLength: 255
                                    }
                                }
                            }}
                            autoFocus
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onClose}
                    variant="text"
                    disabled={isLoading}
                    sx={{
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.action.hover,
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                        },
                    }}
                    disableRipple
                    disableElevation
                    data-testid="cancel-task-button"
                >
                    {t("task.actions.cancel")}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isLoading || (isEditMode && !hasChanges)}
                    sx={{
                        fontWeight: "bold",
                        backgroundColor: (theme) => theme.palette.brand.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.brand.dark,
                            color: (theme) => theme.palette.primary.contrastText,
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                        },
                        "&:disabled": {
                            backgroundColor: "#ccc",
                            color: "#666"
                        }
                    }}
                    disableRipple
                    disableElevation
                    data-testid="save-task-button"
                >
                    {isLoading ? t("task.actions.saving") : (isEditMode ? t("task.actions.updateTask") : t("task.actions.addTask"))}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
